/**
 * CollectionView Component
 * 
 * The main composite component that orchestrates all collection view sub-components.
 * Automatically handles zero/one/few/many states and provides a unified API.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { EmptyState, type EmptyStateVariant } from '../EmptyState';
import { SearchBar } from './SearchBar';
import { FilterBar } from './FilterBar';
import { SortGroupControls, GroupHeader } from './SortGroupControls';
import { ViewModeToggle } from './ViewModeToggle';
import { SelectableList } from './SelectableList';
import { BulkActionToolbar, InlineBulkActions } from './BulkActionToolbar';
import { VirtualizedList } from './VirtualizedList';
import { EntityTable } from './EntityTable';
import { useCollectionState } from './useCollectionState';
import type {
  CollectionViewProps,
  ViewMode,
  FacetDefinition,
  SortOption,
  GroupOption,
  ActiveFilter,
  ItemRenderProps,
} from './types';

// ============================================================================
// Toolbar Component
// ============================================================================

interface CollectionToolbarProps<SortField, FilterField, GroupField> {
  // Search
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchPlaceholder?: string;
  
  // Filtering
  facets?: FacetDefinition<FilterField>[];
  activeFilters: ActiveFilter<FilterField>[];
  onFilterChange: (filters: ActiveFilter<FilterField>[]) => void;
  
  // Sorting
  sortOptions?: SortOption<SortField>[];
  currentSort: { field: SortField; direction: 'asc' | 'desc' };
  onSortChange: (field: SortField, direction?: 'asc' | 'desc') => void;
  
  // Grouping
  groupOptions?: GroupOption<GroupField>[];
  currentGroupBy: GroupField | null;
  onGroupByChange: (field: GroupField | null) => void;
  
  // View mode
  viewModes?: ViewMode[];
  currentViewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  
  className?: string;
}

function CollectionToolbar<SortField = string, FilterField = string, GroupField = string>({
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  facets = [],
  activeFilters,
  onFilterChange,
  sortOptions = [],
  currentSort,
  onSortChange,
  groupOptions = [],
  currentGroupBy,
  onGroupByChange,
  viewModes = ['list'],
  currentViewMode,
  onViewModeChange,
  className = '',
}: CollectionToolbarProps<SortField, FilterField, GroupField>) {
  const hasFilters = facets.length > 0;
  const hasSort = sortOptions.length > 0;
  const hasGroup = groupOptions.length > 0;
  const hasViewModes = viewModes.length > 1;
  
  return (
    <div className={`space-y-4 ${className}`}>
      {/* Top row: Search + View modes */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-64">
          <SearchBar
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
          />
        </div>
        
        <div className="flex items-center gap-3">
          {(hasSort || hasGroup) && (
            <SortGroupControls
              sortOptions={sortOptions}
              currentSort={currentSort}
              onSortChange={onSortChange}
              groupOptions={groupOptions}
              currentGroupBy={currentGroupBy}
              onGroupByChange={onGroupByChange}
            />
          )}
          
          {hasViewModes && (
            <ViewModeToggle
              modes={viewModes}
              currentMode={currentViewMode}
              onModeChange={onViewModeChange}
            />
          )}
        </div>
      </div>
      
      {/* Filter bar */}
      {hasFilters && (
        <FilterBar
          facets={facets}
          activeFilters={activeFilters}
          onFilterChange={onFilterChange}
        />
      )}
    </div>
  );
}

// ============================================================================
// Content Renderers
// ============================================================================

interface ListViewProps<T> {
  items: T[];
  getId: (item: T) => string;
  renderItem: (item: T, props: ItemRenderProps) => ReactNode;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  selectable: boolean;
  onItemClick?: (item: T) => void;
  listStyle?: 'cards' | 'rows';
}

function ListView<T>({
  items,
  getId,
  renderItem,
  selectedIds,
  onSelectionChange,
  selectable,
  onItemClick,
  listStyle = 'cards',
}: ListViewProps<T>) {
  if (!selectable) {
    const isRows = listStyle === 'rows';
    return (
      <div className={isRows
        ? 'bg-[var(--bg-raised)] border border-[var(--rule)]  overflow-hidden divide-y divide-[var(--rule)]'
        : 'space-y-2'
      }>
        {items.map((item) => {
          const id = getId(item);
          const props: ItemRenderProps = {
            selected: false,
            onSelect: () => {},
            onToggle: () => {},
          };
          return (
            <div 
              key={id}
              onClick={() => onItemClick?.(item)}
              className={isRows
                ? `px-4 py-3 hover:bg-[var(--surface)]/60 transition-colors${onItemClick ? ' cursor-pointer' : ''}`
                : (onItemClick ? 'cursor-pointer' : '')
              }
            >
              {renderItem(item, props)}
            </div>
          );
        })}
      </div>
    );
  }
  
  return (
    <SelectableList
      items={items}
      getId={getId}
      renderItem={renderItem}
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      listStyle={listStyle}
    />
  );
}

interface CardViewProps<T> {
  items: T[];
  getId: (item: T) => string;
  renderCard: (item: T, props: ItemRenderProps) => ReactNode;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  selectable: boolean;
  onItemClick?: (item: T) => void;
}

function CardView<T>({
  items,
  getId,
  renderCard,
  selectedIds,
  onToggle,
  selectable,
  onItemClick,
}: CardViewProps<T>) {
  return (
    <div className="p-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 items-start">
      {items.map((item) => {
        const id = getId(item);
        const isSelected = selectedIds.has(id);
        
        const props: ItemRenderProps = {
          selected: isSelected,
          onSelect: () => onToggle(id),
          onToggle: () => onToggle(id),
        };
        
        return (
          <div
            key={id}
            onClick={() => {
              if (selectable) {
                onToggle(id);
              } else {
                onItemClick?.(item);
              }
            }}
            className={`relative  border-2 transition-all cursor-pointer
                        ${isSelected 
                          ? 'border-primary-500 ring-2 ring-primary-200' 
                          : 'border-[var(--rule)] hover:border-[var(--rule)]'
                        }`}
          >
            {renderCard(item, props)}
          </div>
        );
      })}
    </div>
    </div>
  );
}

// ============================================================================
// CollectionView Component
// ============================================================================

export function CollectionView<T, SortField = string, FilterField = string, GroupField = string>({
  items,
  getId,
  renderItem,
  renderCard,
  renderTableRow: _renderTableRow,
  tableColumns: _tableColumns,
  tableColumnsWithRender,
  emptyVariant = 'generic',
  emptyTitle,
  emptyDescription,
  emptyAction,
  facets = [],
  searchFields = [],
  searchPlaceholder = 'Search...',
  sortOptions = [],
  defaultSort,
  groupOptions = [],
  defaultGroupBy,
  selectable = false,
  bulkActions = [],
  viewModes = ['list'],
  defaultViewMode = 'list',
  virtualize,
  virtualizationThreshold = 100,
  estimatedRowHeight = 72,
  virtualizedHeight = 600,
  enableKeyboardNavigation = true,
  pageSize: _pageSize,
  className = '',
  toolbarClassName = '',
  contentClassName = '',
  listStyle = 'cards',
  onSelectionChange,
  onItemClick,
  onItemDoubleClick,
  hideToolbar = false,
  hideStatsBar = false,
  controlledState,
  controlledActions,
}: CollectionViewProps<T, SortField, FilterField, GroupField>) {
  
  // Focused item for keyboard navigation
  const [focusedId, setFocusedId] = useState<string | null>(null);
  
  // Use collection state hook (or controlled state from EntityListShell)
  const [internalState, internalActions] = useCollectionState<T, SortField, FilterField, GroupField>({
    items,
    getId,
    defaultViewMode,
    searchFields: searchFields as (keyof T)[],
    facets: facets as FacetDefinition<FilterField>[],
    sortOptions: sortOptions as SortOption<SortField>[],
    defaultSort,
    groupOptions: groupOptions as GroupOption<GroupField>[],
    defaultGroupBy,
    enableSelection: selectable,
    onSelectionChange,
  });

  const state = controlledState ?? internalState;
  const actions = controlledActions ?? internalActions;
  
  // Determine if we should virtualize
  const shouldVirtualize = virtualize ?? (state.sortedItems.length > virtualizationThreshold);
  
  // Handle select range for shift-click
  const handleSelectRange = useCallback((fromId: string, toId: string) => {
    const fromIndex = state.sortedItems.findIndex(item => getId(item) === fromId);
    const toIndex = state.sortedItems.findIndex(item => getId(item) === toId);
    
    if (fromIndex === -1 || toIndex === -1) return;
    
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    
    const idsToSelect = state.sortedItems.slice(start, end + 1).map(item => getId(item));
    actions.selection.setSelected([...Array.from(state.selection.selectedIds), ...idsToSelect]);
  }, [state.sortedItems, getId, state.selection.selectedIds, actions.selection]);
  
  // Render based on item count state
  const renderContent = useCallback(() => {
    const { sortedItems, groupedItems, viewMode, selection, itemCountState } = state;
    
    // Zero state - show empty state
    if (itemCountState === 'zero') {
      return (
        <EmptyState
          variant={emptyVariant as EmptyStateVariant}
          title={emptyTitle}
          description={emptyDescription}
          actions={emptyAction ? [{
            label: emptyAction.label,
            path: emptyAction.path,
            onClick: emptyAction.onClick,
            primary: true,
          }] : undefined}
        />
      );
    }

    const safeRenderItem =
      renderItem ??
      ((item: T, props: ItemRenderProps) =>
        renderCard ? (
          renderCard(item, props)
        ) : (
          <div className="px-3 py-2 text-sm truncate">
            {String(
              (item as Record<string, unknown>).title ??
                (item as Record<string, unknown>).name ??
                getId(item),
            )}
          </div>
        ));

    // Table view — canonical EntityTable (any entity type)
    if (viewMode === 'table' && tableColumnsWithRender) {
      return (
        <EntityTable
          items={sortedItems}
          getId={getId}
          columns={tableColumnsWithRender}
          selectedIds={selection.selectedIds}
          onToggle={(id) => actions.selection.toggle(id)}
          onSelectRange={handleSelectRange}
          onSelectAll={actions.selection.selectAll}
          onDeselectAll={actions.selection.deselectAll}
          onRowClick={onItemClick}
          onRowDoubleClick={onItemDoubleClick}
          selectable={selectable}
          currentSort={state.sort}
          onSortChange={actions.setSort}
          enableKeyboardNavigation={enableKeyboardNavigation}
          focusedId={focusedId}
          onFocusChange={setFocusedId}
          rowHeight={estimatedRowHeight}
        />
      );
    }
    
    // Grouped view - don't virtualize grouped views for now
    if (groupedItems) {
      return (
        <div className="space-y-4">
          {groupedItems.map((group) => {
            const isExpanded = state.group.expandedGroups.has(group.key);
            
            return (
              <div key={group.key} className="space-y-2">
                <GroupHeader
                  label={group.label}
                  count={group.count}
                  isExpanded={isExpanded}
                  onToggle={() => actions.toggleGroup(group.key)}
                />
                
                {isExpanded && (
                  <div className="pl-4">
                    {viewMode === 'card' && renderCard ? (
                      <CardView
                        items={group.items}
                        getId={getId}
                        renderCard={renderCard}
                        selectedIds={selection.selectedIds}
                        onToggle={(id) => actions.selection.toggle(id)}
                        selectable={selectable}
                        onItemClick={onItemClick}
                      />
                    ) : (
                      <ListView
                        items={group.items}
                        getId={getId}
                        renderItem={safeRenderItem}
                        selectedIds={selection.selectedIds}
                        onSelectionChange={(ids) => actions.selection.setSelected(Array.from(ids))}
                        selectable={selectable}
                        onItemClick={onItemClick}
                        listStyle={listStyle}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    
    // Card view
    if (viewMode === 'card' && renderCard) {
      return (
        <CardView
          items={sortedItems}
          getId={getId}
          renderCard={renderCard}
          selectedIds={selection.selectedIds}
          onToggle={(id) => actions.selection.toggle(id)}
          selectable={selectable}
          onItemClick={onItemClick}
        />
      );
    }

    // List view - virtualize if needed
    if (shouldVirtualize) {
      return (
        <VirtualizedList
          items={sortedItems}
          getId={getId}
          renderItem={safeRenderItem}
          estimatedRowHeight={estimatedRowHeight}
          selectedIds={selection.selectedIds}
          onToggle={(id) => actions.selection.toggle(id)}
          onItemClick={onItemClick}
          onItemDoubleClick={onItemDoubleClick}
          selectable={selectable}
          enableKeyboardNavigation={enableKeyboardNavigation}
          focusedId={focusedId}
          onFocusChange={setFocusedId}
          height={virtualizedHeight}
        />
      );
    }
    
    // Standard list view
    return (
      <ListView
        items={sortedItems}
        getId={getId}
        renderItem={safeRenderItem}
        selectedIds={selection.selectedIds}
        onSelectionChange={(ids) => actions.selection.setSelected(Array.from(ids))}
        selectable={selectable}
        onItemClick={onItemClick}
        listStyle={listStyle}
      />
    );
  }, [
    state, 
    actions, 
    getId, 
    renderItem, 
    renderCard, 
    tableColumnsWithRender,
    selectable, 
    onItemClick,
    onItemDoubleClick,
    emptyVariant,
    emptyTitle,
    emptyDescription,
    emptyAction,
    shouldVirtualize,
    estimatedRowHeight,
    virtualizedHeight,
    enableKeyboardNavigation,
    focusedId,
    handleSelectRange,
    listStyle,
  ]);
  
  return (
    <div className={`${hideToolbar ? '' : 'space-y-6 '}${className}`}>
      {/* Toolbar */}
      {!hideToolbar && (
      <CollectionToolbar
        searchQuery={state.searchQuery}
        onSearchChange={actions.setSearchQuery}
        searchPlaceholder={searchPlaceholder}
        facets={facets}
        activeFilters={state.activeFilters}
        onFilterChange={actions.setFilters}
        sortOptions={sortOptions}
        currentSort={state.sort}
        onSortChange={actions.setSort}
        groupOptions={groupOptions}
        currentGroupBy={state.group.field}
        onGroupByChange={actions.setGroupBy}
        viewModes={viewModes}
        currentViewMode={state.viewMode}
        onViewModeChange={actions.setViewMode}
        className={toolbarClassName}
      />
      )}
      
      {/* Stats bar (for 'few' and 'many' states) */}
      {!hideStatsBar && state.itemCountState !== 'zero' && (
        <div className="flex items-center justify-between text-sm text-[var(--text-sec)]">
          <span>
            {state.filteredCount === state.itemCount 
              ? `${state.itemCount} items`
              : `${state.filteredCount} of ${state.itemCount} items`
            }
          </span>
          
          {state.activeFilters.length > 0 && (
            <button
              type="button"
              onClick={actions.clearFilters}
              className="text-primary-600 hover:text-primary-800 underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}
      
      {/* Bulk actions bar (when items selected) */}
      {selectable && state.selection.selectedIds.size > 0 && bulkActions.length > 0 && (
        <InlineBulkActions
          selectedCount={state.selection.selectedIds.size}
          totalCount={state.filteredCount}
          actions={bulkActions}
          selectedIds={Array.from(state.selection.selectedIds)}
          onSelectAll={actions.selection.selectAll}
          onClearSelection={actions.selection.deselectAll}
        />
      )}
      
      {/* Main content */}
      <div className={contentClassName}>
        {renderContent()}
      </div>
      
      {/* Floating bulk action toolbar (for 'many' state) */}
      {selectable && state.itemCountState === 'many' && bulkActions.length > 0 && (
        <BulkActionToolbar
          selectedCount={state.selection.selectedIds.size}
          totalCount={state.filteredCount}
          actions={bulkActions}
          onSelectAll={actions.selection.selectAll}
          onClearSelection={actions.selection.deselectAll}
        />
      )}
    </div>
  );
}

export default CollectionView;
